import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DialogWrapper } from '../DialogWrapper';
import { useEscKey } from '../../../hooks/useEscKey';

interface EditDialogProps {
  open: boolean;
  onClose: () => void;
  selectedTicket: any;
  editForm: any;
  setEditForm: (form: any) => void;
  onUpdateTicket: () => Promise<void>;
  authUser: any;
  statuses: any[];
  priorities: any[];
  users: any[];
}

export const EditDialog: React.FC<EditDialogProps> = ({
  open,
  onClose,
  selectedTicket,
  editForm,
  setEditForm,
  onUpdateTicket,
  authUser,
  statuses,
  priorities,
  users
}) => {
  useEscKey(open, onClose);

  // Rol bazlı status filtreleme
  const getFilteredStatuses = () => {
    if (authUser?.role_id === 3) { // Customer (role_id: 3)
      // Customer sadece "Kapalı" durumunu görebilir
      return statuses.filter(status => status.id === 8); // Kapalı durumu
    }
    // Support ve Admin tüm statusleri görebilir
    return statuses;
  };

  const filteredStatuses = getFilteredStatuses();

  // Support için "Yeni" durumunu disabled yap
const isStatusDisabled = (statusId: number) => {
  if (authUser?.role_id === 2 && (statusId === 1 || statusId === 2)) { 
    // Support için Yeni (1) ve Atandı (2) durumları
    return true;
  }
  return false;
};

const handleSubmit = async () => {
  try {
    console.log('🔄 EditDialog: Güncelleme başlatılıyor...');
    await onUpdateTicket();
    console.log('✅ EditDialog: Güncelleme başarılı');
  } catch (error) {
    console.error('❌ EditDialog: Güncelleme hatası:', error);
  }
};
  const handleFormChange = (field: string, value: any) => {
    setEditForm((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const isFieldDisabled = (fieldName: string) => {
    if (authUser?.role_id === 3) { // Customer
      if (fieldName === 'status_id' || fieldName === 'notes') {
        return false; // Customer status ve notları değiştirebilir
      }
      return true; // Customer diğer alanları değiştiremez
    }
    return false; // Support ve Admin diğer alanları değiştirebilir
  };

  return (
    <DialogWrapper 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { 
          maxHeight: '95vh',
          marginLeft: '-30px',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          width: '500px'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#2c3e50',
        color: 'white',
        py: 2,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            {authUser?.role_id === 3 ? '🔒 Talep Kapat' : '⚙️ Talebi Yönet'} - #{selectedTicket?.id}
          </Typography>
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Durum */}
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '0.875rem' }}>Durum</InputLabel>
            <Select
              value={editForm.status_id || ''}
              label="Durum"
              onChange={(e) => handleFormChange('status_id', e.target.value)}
              disabled={isFieldDisabled('status_id')}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
                '& .MuiSelect-select': {
                  padding: '10px 14px',
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
            >
              {filteredStatuses.map((status) => (
                <MenuItem 
                  key={status.id} 
                  value={status.id} 
                  disabled={isStatusDisabled(status.id)}
                  sx={{ 
                    fontSize: '0.875rem',
                    opacity: isStatusDisabled(status.id) ? 0.5 : 1
                  }}
                >
                  {status.name}
                  {isStatusDisabled(status.id) && (
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
            {authUser?.role_id === 3 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                Talebi sadece "Kapandı" durumuna getirebilirsiniz
              </Typography>
            )}
            {authUser?.role_id === 2 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                "Yeni" ve "Atandı" durumuna getiremezsiniz!
              </Typography>
            )}
          </FormControl>

          {/* Öncelik - Customer göremez, Support ve Admin görür */}
          {authUser?.role_id == 1 && (
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.875rem' }}>Öncelik</InputLabel>
              <Select
                value={editForm.priority_id || ''}
                label="Öncelik"
                onChange={(e) => handleFormChange('priority_id', e.target.value)}
                disabled={isFieldDisabled('priority_id')}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
              >
                {priorities.map((priority) => (
                  <MenuItem key={priority.id} value={priority.id} sx={{ fontSize: '0.875rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '14px' }}>
                        {priority.id === 1 ? '🟢' : 
                         priority.id === 2 ? '🔵' : 
                         priority.id === 3 ? '🟠' : '🔴'}
                      </span>
                      <span>
                        {priority.id === 1 ? 'Düşük' : 
                         priority.id === 2 ? 'Orta' : 
                         priority.id === 3 ? 'Yüksek' : 'Acil'}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Atanan Kullanıcı - Sadece admin */}
          {authUser?.role_id === 1 && (
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.875rem' }}>Atanan Kullanıcı</InputLabel>
              <Select
                value={editForm.assigned_to || ''}
                label="Atanan Kullanıcı"
                onChange={(e) => handleFormChange('assigned_to', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                  '& .MuiSelect-select': {
                    padding: '10px 14px',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: '0.875rem' }}>Atanmamış</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id} sx={{ fontSize: '0.875rem' }}>
                    {user.name} {user.surname}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Notlar - Tüm kullanıcılar not ekleyebilir */}
          <TextField
            label="Notlar"
            multiline
            rows={4}
            value={editForm.notes || ''}
            onChange={(e) => handleFormChange('notes', e.target.value)}
            placeholder={
              authUser?.role_id === 3 
                ? "Talep kapatma notunuzu ekleyin..." 
                : "Durum değişikliği hakkında not ekleyin..."
            }
            disabled={isFieldDisabled('notes')}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: '0',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem'
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                padding: '12px 14px'
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            minWidth: 100,
            borderColor: '#666',
            color: '#666',
            fontSize: '0.8rem',
            padding: '6px 16px',
            '&:hover': {
              borderColor: '#333',
              backgroundColor: 'rgba(0,0,0,0.04)'
            }
          }}
        >
          İPTAL
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          sx={{ 
            minWidth: 120,
            backgroundColor: '#2c3e50',
            fontSize: '0.8rem',
            padding: '6px 16px',
            '&:hover': {
              backgroundColor: '#1a252f'
            }
          }}
        >
          {authUser?.role_id === 3 ? 'TALEP KAPAT' : 'GÜNCELLE'}
        </Button>
      </DialogActions>
    </DialogWrapper>
  );
};